// src/pages/outsourcing_management/requisitions/RequisitionInquiryLinks.tsx
//
// EE-207 #17, realigned in EE-253 - links to the Inquiry this requisition
// spawned, at /inquiries/:id. The Inquiry is created on submit
// (submit calls ensure_enquiry_for_requisition), so every requisition from
// Submitted onward has one, and it is where all the work after that happens:
// approving it, sending it to the supplier, and every status step up to Ready
// to Use.
//
// Still a list, newest first: rows created before the one-Inquiry rule can
// have several pointing at one requisition (the backend enforces it in
// ensure_enquiry_for_requisition, without a unique index that would hide them).
//
// Fed from RequisitionType.inquiries.
import { FunctionComponent } from "react";
import { Box, Chip, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { RequisitionInquiryLinkType } from "types/RequisitionTypes";
import { reformatDateTime } from "util/DateUtil";
import { InquiryStatusEnum } from "types/InquiryTypes";
import { RequisitionStatusEnum } from "types/RequisitionTypes";
import {
  inquiryStatusLabel,
  INQUIRY_STATUS_COLORS,
} from "../constants/inquiryStatusColors";

type PropsType = {
  inquiries: RequisitionInquiryLinkType[];
  /** Used only to tell "not submitted yet" from "submitted and it went wrong". */
  status?: RequisitionStatusEnum;
  onNavigate?: () => void;
};

// Statuses a requisition can only be in because it was submitted, which is
// what creates the Inquiry.
const PAST_SUBMIT: RequisitionStatusEnum[] = [
  RequisitionStatusEnum.SUBMITTED,
  RequisitionStatusEnum.APPROVED,
  RequisitionStatusEnum.AWAITING_SUPPLIER,
  RequisitionStatusEnum.ORDERED,
  RequisitionStatusEnum.PICKUP_DELIVERY,
  RequisitionStatusEnum.READY_TO_USE,
];

const RequisitionInquiryLinks: FunctionComponent<PropsType> = ({
  inquiries,
  status,
  onNavigate,
}) => {
  if (inquiries.length === 0) {
    // submit commits the transition first and only then calls
    // ensure_enquiry_for_requisition, inside a try/except that logs and moves
    // on. So a submitted requisition with no Inquiry is a real state, and it
    // is stuck: approval and every step after it belong to the Inquiry, and
    // submit is the only thing that makes one - and 409s once the requisition
    // has left Draft.
    // EE-253 P2b: submitting creates no Inquiry. The lines
    // wait in the buyer's Awaiting Inquiry pool until one is built from them.
    if (status && PAST_SUBMIT.includes(status)) {
      return (
        <Typography variant="body2" color="text.secondary">
          No Inquiry yet - the lines are waiting in{" "}
          <RouterLink to="/inquiries?view=awaiting" onClick={onNavigate}>
            Awaiting Inquiry
          </RouterLink>
          .
        </Typography>
      );
    }
    return (
      <Typography variant="body2" color="text.secondary">
        No Inquiry yet - submitting puts the lines in Awaiting Inquiry.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {inquiries.map((inquiry) => (
        <Box key={inquiry.id} sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <RouterLink to={`/inquiries/${inquiry.id}`} onClick={onNavigate}>
            {inquiry.inquiry_no ?? `Inquiry #${inquiry.id}`}
          </RouterLink>
          {inquiry.state && (
            <Chip
              label={inquiryStatusLabel(inquiry.state)}
              size="small"
              sx={INQUIRY_STATUS_COLORS[inquiry.state as InquiryStatusEnum]}
            />
          )}
          <Typography variant="caption" color="text.secondary">
            {[
              inquiry.partner_name,
              inquiry.created ? reformatDateTime(inquiry.created) : null,
            ]
              .filter(Boolean)
              .join(" - ")}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default RequisitionInquiryLinks;
